/**
 * マニュアル本体の編集(#203)。
 *
 * レビューの回答とは別物。作っていくうちに入る「この材料に替える」「この手順にする」
 * という変更を、料理長がそのまま直せるようにする。
 *
 * 画面は依頼を記録するだけで、本文は書き換えない(工程の削除 #174・材料の追加削除 #194 と同じ)。
 * 「回答を本文に反映」で resolve が当て、SOP を組版し直す。
 * 編集の記録は analysis.review.manual_edits に残るので、作り直しても引き継がれる。
 *
 * 材料には原料と歩留まりを登録できる(#216)。「茹でイカ」の原料「アオリイカ」のように、
 * 加工が要る材料を何から作るかを残す。原料が1つなら 分量 ÷ 歩留まり で必要量の目安を出す。
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { deleteDoc, onSnapshot, serverTimestamp, writeBatch } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../auth";
import { useI18n } from "../lib/i18n";
import { fetchFromProxy } from "../lib/session";
import { stepRemovalKey } from "../lib/review";
import { editDocId, type EditDoc } from "../lib/manual-edits";
import {
  draftOf,
  hasInvalidRawMaterial,
  normalizeRawMaterials,
  rowsOf,
  serializeRawMaterials,
  type RawMaterial,
  type RawMaterialRow,
} from "../lib/raw-materials";
import { RawMaterialRows } from "../components/RawMaterialRows";

interface EditableStep {
  step_no: number;
  name: string;
  start: string;
  end: string;
  actions: string;
  heat_level: string | null;
  tools: string[];
  critical_points: string[];
  common_mistakes: string[];
  completion_criteria: { criteria: string | null } | null;
  taste_check: { observed: boolean; criteria: string | null } | null;
  temperature: { oil_c: number | null; note: string | null } | null;
  ingredients: { ingredient_id: string; usage_note?: string | null }[];
}

interface EditableIngredient {
  ingredient_id: string;
  name: string;
  role: "ingredient" | "medium";
  quantity: number | null;
  unit: string | null;
  note: string | null;
  prep_note: string | null;
  raw_materials?: RawMaterial[];
}

interface Manual {
  dish_name: string;
  servings: number | null;
  servings_note: string | null;
  plating_criteria: string | null;
  steps: EditableStep[];
  ingredients_master: EditableIngredient[];
}


/** 原料(#216)は入力中の文字列を持つので、比較・保存は本文と同じ形に揃えてから行う */
function normalizeValue(field: string, value: string): string {
  return field === "raw_materials" ? normalizeRawMaterials(value) : value;
}

export function ManualEdit() {
  const { dishId } = useParams<{ dishId: string }>();
  const { user, paths, profile } = useAuth();
  const isStaff = profile?.role === "admin" || profile?.role === "chef";
  const { t } = useI18n();
  const [manual, setManual] = useState<Manual | null>(null);
  const [edits, setEdits] = useState<Record<string, EditDoc>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!dishId) return;
    setLoadError(null);
    try {
      const r = await fetchFromProxy(`/d/${dishId}/analysis.json`);
      if (!r.ok) {
        throw new Error(
          r.status === 404
            ? t("マニュアルがまだありません。品目の画面で「マニュアルを生成」を実行してください (404)")
            : t("マニュアルの取得に失敗しました ({status})", { status: r.status }),
        );
      }
      setManual((await r.json()) as Manual);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
    }
  }, [dishId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!dishId || !paths) return;
    return onSnapshot(
      paths.manualEdits(dishId),
      (snap) => {
        const next: Record<string, EditDoc> = {};
        snap.docs.forEach((d) => {
          const data = d.data() as EditDoc;
          next[editDocId(data.target, data.key, data.field)] = data;
        });
        setEdits(next);
      },
      (e) => setSaveError(`${t("編集内容の読み込みに失敗しました:")} ${e.message}`),
    );
  }, [dishId, paths]);

  /** 本文の現在値(編集が未反映なら、記録済みの編集を優先して見せる) */
  const valueOf = useCallback(
    (target: EditDoc["target"], key: string, field: string, current: string): string => {
      const id = editDocId(target, key, field);
      if (drafts[id] !== undefined) return drafts[id];
      const saved = edits[id];
      return saved ? saved.value : current;
    },
    [drafts, edits],
  );

  const isEdited = (target: EditDoc["target"], key: string, field: string) =>
    !!edits[editDocId(target, key, field)];
  const isDirty = (target: EditDoc["target"], key: string, field: string, current: string) => {
    const id = editDocId(target, key, field);
    if (drafts[id] === undefined) return false;
    return normalizeValue(field, drafts[id]) !== (edits[id]?.value ?? current);
  };

  const dirtyCount = useMemo(() => Object.keys(drafts).length, [drafts]);
  // 原料(#216)に読めない歩留まり・量があるか。保存すると黙って空になるので止める(#238)
  const invalidRaw = useMemo(
    () => Object.entries(drafts).some(([id, v]) => id.endsWith(":raw_materials") && hasInvalidRawMaterial(v)),
    [drafts],
  );

  /** 入力中のものをまとめて保存。1項目ずつ押させると取りこぼす */
  async function saveAll(currents: Record<string, { target: EditDoc["target"]; key: string; field: string; current: string }>) {
    if (!dishId || !user || !paths || dirtyCount === 0) return;
    if (invalidRaw) {
      setSaveError(t("原料の歩留まり・量に数値でない値があります。赤字の欄を直してから保存してください。"));
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const ids = Object.keys(drafts);
      for (let i = 0; i < ids.length; i += 400) {
        const batch = writeBatch(db);
        for (const id of ids.slice(i, i + 400)) {
          const meta = currents[id];
          if (!meta) continue;
          const value = normalizeValue(meta.field, drafts[id]);
          const ref = paths.manualEditDoc(dishId, id);
          // 元の本文と同じに戻したら、編集の記録そのものを消す(差分だけを持つ)
          if (value === meta.current) batch.delete(ref);
          else
            batch.set(ref, {
              target: meta.target,
              key: meta.key,
              field: meta.field,
              value,
              editedBy: user.uid,
              editedAt: serverTimestamp(),
            });
        }
        await batch.commit();
      }
      setDrafts({});
      setSavedNote(t("{n}件の編集を保存しました。品目の画面の「回答を本文に反映」でマニュアルに反映されます。", { n: ids.length }));
    } catch (e) {
      setSaveError(`${t("保存に失敗しました:")} ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  }

  /** 記録済みの編集を取り消す(本文の値に戻す) */
  async function revert(target: EditDoc["target"], key: string, field: string) {
    if (!dishId || !paths) return;
    const id = editDocId(target, key, field);
    setSaving(true);
    try {
      await deleteDoc(paths.manualEditDoc(dishId, id));
      setDrafts((d) => {
        const next = { ...d };
        delete next[id];
        return next;
      });
    } catch (e) {
      setSaveError(`${t("取り消しに失敗しました:")} ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  }

  if (!isStaff) {
    return (
      <div className="page">
        <p><Link to={`/dishes/${dishId}`}>{t("← 品目")}</Link></p>
        <p className="muted">{t("マニュアルの編集は管理者と料理長のみ行えます。")}</p>
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="page">
        <p><Link to={`/dishes/${dishId}`}>{t("← 品目")}</Link></p>
        <p className="error">{loadError}</p>
        <button onClick={() => void load()}>{t("もう一度読み込む")}</button>
      </div>
    );
  }
  if (!manual) return <div className="page"><p className="muted">{t("読み込み中…")}</p></div>;

  // 保存時に「元の値」を引けるようにしておく(元に戻したら記録を消すため)
  const currents: Record<string, { target: EditDoc["target"]; key: string; field: string; current: string }> = {};
  const reg = (target: EditDoc["target"], key: string, field: string, current: string) => {
    currents[editDocId(target, key, field)] = { target, key, field, current };
    return current;
  };

  /** 1項目分の入力欄。編集済みなら印と取り消しを出す */
  function field(
    target: EditDoc["target"],
    key: string,
    name: string,
    label: string,
    current: string,
    opts: { rows?: number; placeholder?: string } = {},
  ) {
    reg(target, key, name, current);
    const id = editDocId(target, key, name);
    const value = valueOf(target, key, name, current);
    const edited = isEdited(target, key, name);
    const dirty = isDirty(target, key, name, current);
    return (
      <div className={`edit-field${edited ? " edited" : ""}${dirty ? " dirty" : ""}`} key={id}>
        <label>
          {label}
          {edited && <span className="badge p-medium">{t("編集済み")}</span>}
          {edited && (
            <button className="link" disabled={saving} onClick={() => void revert(target, key, name)}>
              {t("元に戻す")}
            </button>
          )}
        </label>
        {opts.rows && opts.rows > 1 ? (
          <textarea
            rows={opts.rows}
            value={value}
            placeholder={opts.placeholder}
            onChange={(e) => setDrafts((d) => ({ ...d, [id]: e.target.value }))}
          />
        ) : (
          <input
            type="text"
            value={value}
            placeholder={opts.placeholder}
            onChange={(e) => setDrafts((d) => ({ ...d, [id]: e.target.value }))}
          />
        )}
      </div>
    );
  }

  const foods = manual.ingredients_master.filter((m) => m.role !== "medium");
  const amountOf = (m: EditableIngredient) => (m.quantity != null ? String(m.quantity) : "");

  /**
   * 原料と歩留まり(#216)。1行1原料。記録は1項目(raw_materials)として保存する。
   * 必要量の目安は、編集中の分量・単位も使って出す(保存前に確かめられるように)
   */
  function rawField(m: EditableIngredient) {
    const current = reg("ingredient", m.name, "raw_materials", serializeRawMaterials(m.raw_materials));
    const id = editDocId("ingredient", m.name, "raw_materials");
    const rows = rowsOf(valueOf("ingredient", m.name, "raw_materials", current));
    const edited = isEdited("ingredient", m.name, "raw_materials");
    const dirty = isDirty("ingredient", m.name, "raw_materials", current);
    const qtyText = valueOf("ingredient", m.name, "quantity", amountOf(m)).trim();
    const parent = {
      quantity: qtyText && Number.isFinite(Number(qtyText)) ? Number(qtyText) : null,
      unit: valueOf("ingredient", m.name, "unit", m.unit ?? "").trim() || null,
    };
    const setRows = (next: RawMaterialRow[]) => setDrafts((d) => ({ ...d, [id]: draftOf(next) }));
    return (
      <div className={`edit-field raw-materials${edited ? " edited" : ""}${dirty ? " dirty" : ""}`}>
        <label>
          {t("原料（この材料を作るのに使うもの）")}
          {edited && <span className="badge p-medium">{t("編集済み")}</span>}
          {edited && (
            <button className="link" disabled={saving} onClick={() => void revert("ingredient", m.name, "raw_materials")}>
              {t("元に戻す")}
            </button>
          )}
        </label>
        <RawMaterialRows rows={rows} parent={parent} onChange={setRows} />
      </div>
    );
  }

  return (
    <div className="page">
      <p><Link to={`/dishes/${dishId}`}>{t("← 品目")}</Link></p>
      <header className="page-head">
        <h1>{t("マニュアルの編集: {name}", { name: manual.dish_name })}</h1>
      </header>
      <p className="muted small">
        {t("作っていくうちに決まった内容（この材料に替える・この手順にする）を直せます。確認事項への回答とは別で、ここで直した内容が本文の正になります。")}
        {t("直した項目は「編集済み」と出ます。保存したあと、品目の画面の「回答を本文に反映」でマニュアルへ落ちます。作り直しても編集は引き継がれます。")}
      </p>

      <section className="step-block">
        <h2>{t("全体")}</h2>
        <div className="card">
          {field("dish", "", "servings", t("人数（何皿分）"), manual.servings != null ? String(manual.servings) : "", { placeholder: t("例: 2") })}
          {field("dish", "", "servings_note", t("人数の補足"), manual.servings_note ?? "", { placeholder: t("例: 一皿分") })}
          {field("dish", "", "plating_criteria", t("盛り付け基準"), manual.plating_criteria ?? "", { rows: 3 })}
        </div>
      </section>

      <section className="step-block">
        <h2>{t("材料・調味料")}</h2>
        <p className="muted small">{t("材料の追加・削除は「レビュー・回答」の材料表で行います。ここでは名前と分量を直します。")}
          {t("下処理や加熱が要る材料は、原料と歩留まりを登録できます（例: 茹でイカの原料はアオリイカ、歩留まり60%）。原料の量を空けておくと、分量と歩留まりから必要量の目安を出します。")}</p>
        {foods.map((m) => (
          <div className="card" key={m.ingredient_id}>
            <div className="edit-row">
              {field("ingredient", m.name, "name", t("名前"), m.name)}
              {field("ingredient", m.name, "quantity", t("分量（数値）"), amountOf(m), { placeholder: t("例: 225") })}
              {field("ingredient", m.name, "unit", t("単位"), m.unit ?? "", { placeholder: t("例: g") })}
            </div>
            {field("ingredient", m.name, "note", t("備考"), m.note ?? "", { placeholder: t("例: 8切れ") })}
            {field("ingredient", m.name, "prep_note", t("説明（下処理・何から作るか）"), m.prep_note ?? "", {
              rows: 2,
              placeholder: t("例: 光中杏・砂糖・水から作ったもの"),
            })}
            <p className="muted small">{t("材料表で「— 」のあとに出る説明です。空にして保存すると説明を消します。")}</p>
            {rawField(m)}
          </div>
        ))}
      </section>

      {manual.steps.map((st) => {
        const key = stepRemovalKey(st);
        return (
          <section className="step-block" key={key}>
            <h2>
              {t("工程{n}:", { n: st.step_no })} {st.name}
              <span className="muted small">　{st.start}〜{st.end}</span>
            </h2>
            <div className="card">
              {field("step", key, "name", t("工程名"), st.name)}
              {field("step", key, "actions", t("作業内容"), st.actions, { rows: 4 })}
              <div className="edit-row">
                {field("step", key, "heat_level", t("火加減"), st.heat_level ?? "", { placeholder: t("例: 強火") })}
                {field("step", key, "temperature_c", t("油温（℃）"), st.temperature?.oil_c != null ? String(st.temperature.oil_c) : "", { placeholder: t("例: 170") })}
              </div>
              {field("step", key, "temperature_note", t("油温の目安（状態）"), st.temperature?.note ?? "", { placeholder: t("例: 煙が薄く立つ程度") })}
              {field("step", key, "tools", t("使用器具（1行に1つ）"), st.tools.join("\n"), { rows: 2 })}
              {field("step", key, "critical_points", t("重要ポイント（1行に1つ）"), st.critical_points.join("\n"), { rows: 3 })}
              {field("step", key, "common_mistakes", t("ありがちなミス（1行に1つ）"), st.common_mistakes.join("\n"), { rows: 3 })}
              {field("step", key, "completion_criteria", t("完了基準"), st.completion_criteria?.criteria ?? "", { rows: 2 })}
              {field("step", key, "taste_check", t("味見の基準"), st.taste_check?.criteria ?? "", { rows: 2 })}
              {/* この工程で使う材料ごとの使い方(SOP の「投入する食材」の括弧書き)。
                  材料は名前で同定する(CLI の usage_note:<材料名>) */}
              {st.ingredients.map((r) => {
                const m = manual.ingredients_master.find((x) => x.ingredient_id === r.ingredient_id);
                if (!m || m.role === "medium") return null;
                return (
                  <Fragment key={r.ingredient_id}>
                    {field("step", key, `usage_note:${m.name}`, t("「{name}」の使い方（この工程）", { name: m.name }), r.usage_note ?? "", {
                      placeholder: t("例: 半量を使用"),
                    })}
                  </Fragment>
                );
              })}
            </div>
          </section>
        );
      })}

      {saveError && <p className="error">{saveError}</p>}
      {(dirtyCount > 0 || savedNote) && (
        <div className="save-bar">
          {dirtyCount > 0 ? (
            <>
              <span>{t("未保存 {n}件", { n: dirtyCount })}</span>
              {invalidRaw && (
                <span className="error small">{t("原料の歩留まり・量に数値でない値があります。赤字の欄を直してから保存してください。")}</span>
              )}
              <button onClick={() => void saveAll(currents)} disabled={saving || invalidRaw}>
                {saving ? t("保存中…") : t("編集をまとめて保存")}
              </button>
            </>
          ) : (
            <span className="ok">{savedNote}</span>
          )}
        </div>
      )}
    </div>
  );
}
